import React, { FormEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { wsAxiosPut } from "src/helpers/wsAxios";
import { CustomSwitch } from "src/app/components/CustomSwitch/CustomSwitch";
import { taskAvatarShape } from "src/app/components/Table/Table";
import { TaskType } from "src/types";
import { ITask } from "src/redux/task/taskReducer";
import { RootState } from "src/redux/reducers";
import { showErrorToast } from "src/utils/methods";
import Modal, {
  ModalText,
  ModalTitle,
  OutsideClickModes,
} from "../../components/Modal/Modal";
import { showToast } from "../../methods/showToast";
import {
  localSetColumnsGroupSharing,
  localSetTasksSharing,
  setSelectedTasks,
} from "../../../redux";

import { API_URLS } from "../../../utils/API_URLS";
import CustomCheckbox from "../../components/CustomCheckbox/CustomCheckbox";
import ShareMultipleColumnsTaskDetails from "./ShareMultipleTaskDetails";

import "./ShareMultipleColumns.scss";

interface ShareMultipleColumnsModalProps {
  onClose: () => void;
  avatarShape: taskAvatarShape;
  tasks: ITask[];
  taskType: TaskType;
  shouldSelectTasksAfterSharing?: boolean;
}

function ShareMultipleColumnsModal({
  onClose,
  avatarShape,
  tasks,
  taskType,
  shouldSelectTasksAfterSharing = false,
}: ShareMultipleColumnsModalProps) {
  const {
    taskReducer: { tasksColumns },
    projectReducer: { projectBasicData },
  } = useSelector((state: RootState) => state);

  const [isLoading, setIsLoading] = useState(false);

  const [fieldsList, setFieldsList] = useState(
    tasksColumns.map(
      ({
        metaFieldId,
        metaFieldShared,
        metaFieldIsSystem,
        metaFieldSharedEditable,
      }) => ({
        id: metaFieldId,
        shared: metaFieldIsSystem || metaFieldShared,
        editable: metaFieldSharedEditable,
      }),
    ),
  );

  const dispatch = useDispatch();

  const shareWithClient = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const sharedColumnsData = fieldsList
      .filter((checkbox) => checkbox.shared)
      .map((checkbox) => ({
        uuid: checkbox.id,
        editable: checkbox.editable,
      }));

    setIsLoading(true);

    try {
      await axios.post(
        API_URLS.shareMultipleColumns.replace(
          ":wsProjectUuid:",
          projectBasicData?.projectId ?? "",
        ),
        {
          wsTaskMetaFieldUuids: sharedColumnsData,
          wsTaskType: taskType,
        },
      );

      dispatch(localSetColumnsGroupSharing({ sharedColumnsData }));
      const taskIds = tasks.map((task) => task.taskId);

      await wsAxiosPut(
        API_URLS.shareGroupTask,
        {
          wsTaskUuids: taskIds,
        },
        "Error occurred during sharing column",
        () => {},
        () => {
          showToast(
            "success",
            <IDHFormattedMessage id="ws_sharing" defaultMessage="Sharing" />,
            <IDHFormattedMessage
              id="ws_sharing_for_selected_enabled"
              defaultMessage="You have successfully shared the data with the client(s)."
            />,
          );
          dispatch(localSetTasksSharing(taskIds, true));
          if (shouldSelectTasksAfterSharing) {
            dispatch(
              setSelectedTasks(
                tasks.map((task) => ({
                  ...task,
                  shared: true,
                })),
              ),
            );
          }
        },
      );
      onClose();
    } catch (error) {
      console.error(error);
      showErrorToast({
        id: "ws_cannot_share_fields",
        defaultMessage: "Cannot share fields",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleEditable = (metaFieldId: string) => {
    setFieldsList((prev) =>
      prev.map((field) =>
        field.id === metaFieldId
          ? { ...field, editable: !field.editable }
          : field,
      ),
    );
  };

  const onSharedChange = (metaFieldId: string) => {
    setFieldsList((prev) =>
      prev.map((field) =>
        field.id === metaFieldId ? { ...field, shared: !field.shared } : field,
      ),
    );
  };

  return (
    <Modal
      isLoading={isLoading}
      className="share-multiple-columns"
      onClose={onClose}
      displayCancelButton
      closeButtonVariant="white-with-light-grey-border"
      closeButtonText={
        <IDHFormattedMessage id="ws_cancel" defaultMessage="Cancel" />
      }
      onConfirmClick={shareWithClient}
      confirmButtonText={
        <IDHFormattedMessage id="ws_share" defaultMessage="Share" />
      }
      outsideClick={OutsideClickModes.Warn}
    >
      <ModalTitle>
        <IDHFormattedMessage
          id="ws_share_with_client"
          defaultMessage="Share with Client"
        />
      </ModalTitle>

      <ShareMultipleColumnsTaskDetails
        tasks={tasks}
        taskAvatarShape={avatarShape}
        taskType={taskType}
      />

      <ModalText>
        <IDHFormattedMessage
          id="ws_share_multiple_columns_modal_text"
          defaultMessage="Please select the columns you want to share. The columns visibility will be applied to all shared items."
        />
      </ModalText>

      <form onSubmit={shareWithClient}>
        <div className="share-multiple-columns__header">
          <div>
            <IDHFormattedMessage id="ws_creator" defaultMessage="Creator" />
          </div>

          <div className="share-multiple-columns__header-editable-label">
            <IDHFormattedMessage
              id="ws_editable_for_client"
              defaultMessage="Editable for Client"
            />
          </div>
        </div>

        <div className="share-multiple-columns__checkbox-wrapper">
          {tasksColumns.map(
            ({ metaFieldName, metaFieldId, metaFieldIsSystem }) => {
              const currentField = fieldsList.find(
                (field) => field.id === metaFieldId,
              );

              if (currentField) {
                return (
                  <div
                    key={`checkbox-${metaFieldName}`}
                    className="share-multiple-columns__field-wrapper"
                  >
                    <CustomCheckbox
                      className="share-multiple-columns__checkbox"
                      id={`${metaFieldName}-checkbox`}
                      name={`${metaFieldName}-checkbox`}
                      label={metaFieldName}
                      disabled={metaFieldIsSystem}
                      checked={currentField.shared}
                      onChange={() => onSharedChange(metaFieldId)}
                    />

                    <CustomSwitch
                      checked={currentField.editable}
                      onChange={() => toggleEditable(metaFieldId)}
                      disabled={metaFieldIsSystem || !currentField.shared}
                    />
                  </div>
                );
              }
              return null;
            },
          )}
        </div>
      </form>
    </Modal>
  );
}

export { ShareMultipleColumnsModal };
